import "./index.scss";
import { BreakpointProvider } from "utilities/BreakpointProvider";
import { GlobalProvider } from "utilities/GlobalProvider";
import { HeaderLiteSchema } from "components/HeaderNew/HeaderLite/CfHeaderLite/schema";
import { HeaderMenuSchema } from "components/HeaderNew/HeaderFull/CfHeaderMenuFull/schema";
import { LayoutProps, TranslationProps } from "./types";
import { MenuProvider } from "utilities/MenuProvider";
import { ModalProvider } from "utilities/ModalProvider";
import {
  Transition as ReactTransition,
  TransitionStatus,
} from "react-transition-group";
import { convertCfHeaderMenuLiteProps } from "components/HeaderNew/HeaderLite/CfHeaderLite";
import {
  convertCfHeaderMenuProps,
  normalizeCfJsonData,
} from "components/HeaderNew/HeaderFull/CfHeaderMenuFull/CfHeaderMenu";
import { logDebug } from "helpers/generic";
import { mediaQueries } from "../../configs/constants";
import { useContentfulLiveUpdates } from "@contentful/live-preview/react";
import {
  useRegisterControllerScrollAnimations,
  useSiteContext,
} from "context/SiteProvider";
import Breadcrumb from "../Breadcrumb";
import BtnBackToTop from "../BtnBackToTop";
import CfBannerDownloadApp from "components/BannerDownloadApp/CfBannerDownloadApp";
import CfBannerPopup from "components/BannerPopup/CfBannerPopup";
import CfFooter from "components/Footer/CfFooter/CfFooter";
import CookieBanner from "../CookieBanner";
import Header from "components/Header";
import HeaderFull from "components/HeaderNew/HeaderFull";
import HeaderLite from "components/HeaderNew/HeaderLite";
import React, {
  Suspense,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import SearchOverlay from "../Search/SearchOverlay";
import cn from "classnames";

const COMPONENTS_WITHOUT_HEADER_PADDING = [
  "ContentfulOpenBanner",
  "ContentfulSliderV2",
  "ContentfulHeroBanner",
];

const Transition = ({ pathname }: TranslationProps) => {
  const [inProp, setInProp] = useState(true);
  const { state, dispatch } = useSiteContext();
  const showLoader = useRef(true);
  const timeout = useRef<NodeJS.Timeout | undefined>(undefined);

  const TIMEOUT_PAGE_LOADER = state?.timeoutTransition || 0;

  const transitionStyles: { [key in TransitionStatus]?: any } = {
    entering: { opacity: 1 },
    entered: { opacity: 1 },
    exiting: { opacity: 0 },
    exited: { opacity: 0 },
  };

  const canShowLoader = () => {
    showLoader.current = false;
  };

  useLayoutEffect(() => {
    if (state && !state.viewTransitionLoader) {
      if (state.visitedPagesBeforeLoader.length > 1)
        dispatch?.({ type: "view_transition_loader", payload: true });
      if (!state.visitedPagesBeforeLoader.includes(pathname))
        dispatch?.({ type: "add_visited_page", payload: pathname });
    }
  }, [dispatch, pathname, state]);

  return (
    <ReactTransition
      key={pathname}
      in={inProp}
      onExiting={canShowLoader}
      timeout={0}
    >
      {(status) => {
        if (status === "entered") {
          clearTimeout(timeout.current);
          timeout.current = setTimeout(() => {
            setInProp(false);
          }, TIMEOUT_PAGE_LOADER);
        }
        return (
          <div
            id="pageLoader"
            className={cn("loader", {
              "loader--show":
                status !== "exited" &&
                state?.viewTransitionLoader &&
                showLoader.current,
            })}
            style={transitionStyles[status]}
          >
            <div className="loader__content" />
          </div>
        );
      }}
    </ReactTransition>
  );
};

const Layout = (props: LayoutProps) => {
  const {
    bannerApp,
    bannerPopup,
    breadcrumb,
    children,
    headConfig,
    header,
    settings,
    hasScrollAnimation,
    dataJsonMenu,
  } = props;

  const { pageHrefLang, lang, location } = headConfig;
  const registerControllerScrollAnimations =
    useRegisterControllerScrollAnimations();

  const langs = Object.entries(pageHrefLang ?? {}).map(([key, value]) => {
    return {
      ctaType: "internalLink",
      internalLink: {
        node_locale: key,
        slug: value,
      },
      label: key,
      link: `/${key}/${value}`,
      current: lang === key,
    };
  });

  const currentPath = pageHrefLang[lang];
  const pathArray = currentPath.split("/");

  const currentHeader = header[0];

  logDebug("LANG", langs);

  const privateArea = currentHeader.settings?.privateArea
    ? currentHeader.settings.privateArea[0]
    : undefined;

  let defaultPageMenu;
  if (
    currentHeader.settings?.defaultPageMenu &&
    currentHeader.settings.defaultPageMenu.length
  ) {
    defaultPageMenu = currentHeader?.settings?.defaultPageMenu[0]?.slug;
    defaultPageMenu = defaultPageMenu.split("/");
    [defaultPageMenu] = defaultPageMenu;
  }

  const currentPageParentSlug = defaultPageMenu || pathArray[0];

  const currentHeaderTranslation = currentHeader.settings?.translations || [];
  const searchDefaultPage = currentHeader.settings?.defaultPageSearch
    ? currentHeader.settings.defaultPageSearch[0]
    : undefined;
  const searchPageSlug = searchDefaultPage?.slug;
  const searchText =
    (currentHeader.settings?.textSearch?.textSearch as string) || "";

  const hasMenuTop =
    // eslint-disable-next-line no-underscore-dangle
    currentHeader.content[0].__typename === "ContentfulMenuTopV2";

  useEffect(() => {
    const destryController = registerControllerScrollAnimations?.(
      hasScrollAnimation ?? undefined,
    );

    return () => {
      destryController?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      const chatWrapper: HTMLElement | null =
        document.querySelector("#iaw-container");

      if (chatWrapper) {
        const chatPathList = ["p/rete-assistenza"];
        const chatNotVisibile =
          lang === "en" ||
          chatPathList.includes(currentPath) ||
          // (double check with gatsby-browser.js to be safe)
          currentPath.includes("supporto");
        chatWrapper.style.display = chatNotVisibile ? "none" : "block";
      }
    }, 1000);

    return () => {
      clearTimeout(t);
    };
  }, [lang, currentPath]);

  // salesforce chatbot render management (double check with gatsby-browser.js to be safe)
  useEffect(() => {
    const isSupportPage = currentPath.includes("supporto");
    const t = setTimeout(() => {
      const salesforceChatbot = document.getElementById("embedded-messaging");
      if (salesforceChatbot) {
        salesforceChatbot.style.display = isSupportPage ? "block" : "none";
      }
    }, 1000);

    return () => {
      clearTimeout(t);
    };
  }, [currentPath]);

  // NOTE: don't remove, reset attribute to automatic `gatsby-focus-wrapper`. for fix problem in detail accordio
  useEffect(() => {
    const gatsbyFocusWrapper = document.getElementById("gatsby-focus-wrapper");
    if (gatsbyFocusWrapper) {
      gatsbyFocusWrapper.removeAttribute("style");
      gatsbyFocusWrapper.removeAttribute("tabIndex");
    }
  }, []);

  const footerData = {
    footerJson: settings.footer.configuration.internal.content,
    contentful_id: settings.footer.contentful_id,
    typename: settings.footer.__typename,
  };

  const getChildrenWithBreadcrumb = () => {
    const [firstChild, ...restChildren] = React.Children.toArray(children);

    const breadcrumbComponent = breadcrumb ? (
      <Breadcrumb {...breadcrumb} />
    ) : null;

    return [
      COMPONENTS_WITHOUT_HEADER_PADDING.includes(
        typeof firstChild === "object" &&
          "props" in firstChild &&
          Array.isArray(firstChild.props.children) &&
          firstChild.props.children[0].props.__typename,
      ) ? (
        <div className="header-position--over">{firstChild}</div>
      ) : (
        <div className="header-position--top">{firstChild}</div>
      ),
      breadcrumbComponent,
      ...restChildren,
    ];
  };

  const { navigation: _, ...liveDataJson } = useContentfulLiveUpdates({
    ...dataJsonMenu,
    sys: { id: dataJsonMenu?.contentful_id },
  });

  const navigationliveDataJson = useContentfulLiveUpdates({
    ...dataJsonMenu?.navigation[0],
    sys: { id: dataJsonMenu?.navigation?.[0].contentful_id },
  });

  const normalizedLiveDataJson = normalizeCfJsonData(
    // @ts-expect-error liveDataJson has every fields as optional because of useContentfulLiveUpdates process.
    // If no data, it doesn't return a simple undefined but an object with the field sys as undefined
    Object.keys(liveDataJson).length > 1
      ? { ...liveDataJson, navigation: navigationliveDataJson }
      : undefined,
  );

  const isHeaderFullJson = (
    jsonData: HeaderMenuSchema | HeaderLiteSchema,
  ): jsonData is HeaderMenuSchema => {
    return (jsonData as HeaderMenuSchema).menu[0].submenu !== undefined;
  };

  return (
    <>
      <Transition pathname={location.href} />
      <GlobalProvider>
        <BreakpointProvider queries={mediaQueries}>
          <ModalProvider>
            <MenuProvider
              langs={langs}
              currentPageParentSlug={currentPageParentSlug}
              initLevel={hasMenuTop ? 1 : 0}
              privateArea={privateArea}
            >
              {bannerApp && (
                <CfBannerDownloadApp
                  contentful_id={bannerApp.contentful_id}
                  imageIconApp={bannerApp.imageIconApp}
                  title={bannerApp.title}
                  text={bannerApp.text}
                  buttonLabel={bannerApp.buttonLabel}
                  buttonLink={bannerApp.buttonLink}
                />
              )}
              {normalizedLiveDataJson?.menu ? (
                <>
                  {isHeaderFullJson(normalizedLiveDataJson) && (
                    <HeaderFull
                      data={convertCfHeaderMenuProps({
                        menu: { dataJson: normalizedLiveDataJson },
                      })}
                      searchContent={
                        normalizedLiveDataJson.settings.search.slug && (
                          <SearchOverlay
                            translations={currentHeaderTranslation}
                            currentLang={lang}
                            searchPageSlug={
                              normalizedLiveDataJson.settings.search.slug
                            }
                            text={normalizedLiveDataJson.settings.search.text}
                          />
                        )
                      }
                      location={location}
                    />
                  )}
                  {!isHeaderFullJson(normalizedLiveDataJson) && (
                    <HeaderLite
                      data={convertCfHeaderMenuLiteProps({
                        dataJson: normalizedLiveDataJson,
                      })}
                    />
                  )}
                </>
              ) : (
                <Header
                  data={currentHeader}
                  searchContent={
                    searchPageSlug && (
                      <SearchOverlay
                        translations={currentHeaderTranslation}
                        currentLang={lang}
                        searchPageSlug={searchPageSlug}
                        text={searchText}
                      />
                    )
                  }
                />
              )}
            </MenuProvider>
            <div>
              <Suspense>
                {getChildrenWithBreadcrumb()}
                {footerData.footerJson && (
                  <CfFooter
                    dataJson={JSON.parse(footerData.footerJson)}
                    identifiers={{
                      contentful_id: footerData.contentful_id,
                      __typename: footerData.typename,
                    }}
                  />
                )}
              </Suspense>
            </div>
            <CookieBanner />
            <BtnBackToTop />
            {bannerPopup && <CfBannerPopup {...bannerPopup} />}
          </ModalProvider>
        </BreakpointProvider>
      </GlobalProvider>
    </>
  );
};

export default Layout;
